import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ContentBlock } from '../../../../../../../../../../../backend/src/types/types';
import { CopyIconButton } from '@/new/components/copy-icon-button';
import { Icon } from '@/new/components/icon';

export function UserMessage({ content, createdAt }: { content: ContentBlock[]; createdAt?: string }) {
  const { matterId, subsetId } = useParams();
  const { i18n } = useTranslation();

  const rendered = useMemo(() => {
    const raw = content
      .filter((block) => block.type === 'text')
      .map((block) => block.value)
      .join('');

    const parts: (string | JSX.Element)[] = [];
    const docMentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = docMentionRegex.exec(raw)) !== null) {
      if (match.index > lastIndex) {
        parts.push(raw.slice(lastIndex, match.index));
      }
      const [, fileName, docId] = match;
      parts.push(
        <a
          key={match.index}
          href={`/matters/${matterId}/subsets/${subsetId}/docs/${docId}`}
          target="_blank"
          className="group text-blue-500 duration-150 hover:text-blue-600"
        >
          {fileName}
          <Icon className="lucide--external-link mr-0.5 -mb-px ml-1.25 inline-block text-xs" />
        </a>,
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < raw.length) {
      parts.push(raw.slice(lastIndex));
    }

    return parts.some((p) => typeof p !== 'string') ? parts : raw;
  }, [content, matterId, subsetId]);

  return (
    <div className="font-inter mx-auto flex max-w-3xl justify-end px-4" data-user-message="">
      <div className="group flex grow flex-col items-end gap-1.75 pt-8 pb-4">
        <div className="max-w-4/5 rounded-xl rounded-br-none border border-gray-300 bg-gray-100 px-3.75 py-2.25 [font-feature-settings:'calt'_0]">
          <div className="text-gray-600">{rendered}</div>
        </div>

        <div className="flex items-center gap-1 text-xs text-gray-400 opacity-0 duration-150 group-hover:opacity-100">
          {createdAt && (
            <p>{new Date(createdAt).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })}</p>
          )}
          <CopyIconButton
            getText={() => {
              const raw = content
                .filter((block) => block.type === 'text')
                .map((block) => block.value)
                .join('');
              return raw.replace(/@\[([^\]]+)\]\([^)]+\)/g, '$1');
            }}
            className="text-xs text-gray-400 duration-150 hover:text-gray-500"
          />
        </div>
      </div>
    </div>
  );
}
